// Sparkline — tiny inline SVG trend line with a soft area fill. Purely
// decorative: it always carries aria-hidden, so consumers must pair it with
// adjacent text (a sub-label or meta line) that conveys the same trend.
//
// Pattern rule: a chrome-level trend (hero band, stat block) is a decorative
// sparkline (aria-hidden) + adjacent text counterpart — this component; a
// data-level chart is a labelled SVG (role="img" + aria-label) — see
// manage/tracking/kpi/overview/KpiSparkline.tsx for that counterpart.

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
}

export default function Sparkline({
  data,
  width = 96,
  height = 28,
  color = "currentColor",
  strokeWidth = 2,
  className,
}: SparklineProps) {
  if (data.length === 0) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1; // flat data still draws a (level) line
  const pad = strokeWidth;
  const stepX = (width - pad * 2) / (data.length - 1 || 1); // single point can't divide by zero

  const points = data.map((value, i) => {
    const x = pad + i * stepX;
    const y = pad + (1 - (value - min) / span) * (height - pad * 2);
    return [x, y] as const;
  });

  const line = points
    .map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L ${points[points.length - 1][0].toFixed(1)} ${height} L ${points[0][0].toFixed(1)} ${height} Z`;

  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      aria-hidden="true"
    >
      <path className="opacity-[0.14]" d={area} fill={color} />
      <path
        d={line}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
